export const ACTIVE_RIDE_STATUSES = new Set([
  'in_progress',
  'arrived',
  'accepted',
  'open',
  'dispatcher_pending',
]);

export const COMPLETED_RIDE_STATUSES = new Set([
  'completed',
  'cancelled',
  'cancelled_by_driver',
]);

export const toMs = (t) => {
  if (!t) return null;
  if (t.toDate) return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  const ms = new Date(t).getTime();
  return Number.isNaN(ms) ? null : ms;
};

export const startOfDayMs = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
};

export const endOfDayMs = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.getTime();
};

export const getTodayPlus12hBounds = (nowInput = Date.now()) => {
  const now = new Date(nowInput);
  return {
    start: startOfDayMs(now),
    end: now.getTime() + 12 * 60 * 60 * 1000,
  };
};

export const getTimeRangeBounds = (range, customStart, customEnd, nowInput = Date.now()) => {
  if (range === 'all') return null;
  const now = new Date(nowInput);

  switch (range) {
    case 'today_plus_12h':
      return getTodayPlus12hBounds(now);
    case 'next_24h':
      return { start: now.getTime(), end: now.getTime() + 24 * 60 * 60 * 1000 };
    case 'today':
      return { start: startOfDayMs(now), end: endOfDayMs(now) };
    case 'tomorrow': {
      const t = new Date(now);
      t.setDate(t.getDate() + 1);
      return { start: startOfDayMs(t), end: endOfDayMs(t) };
    }
    case 'this_week': {
      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: startOfDayMs(monday), end: endOfDayMs(sunday) };
    }
    case 'next_week': {
      const day = now.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const nextMonday = new Date(now);
      nextMonday.setDate(now.getDate() + diffToMonday + 7);
      const nextSunday = new Date(nextMonday);
      nextSunday.setDate(nextMonday.getDate() + 6);
      return { start: startOfDayMs(nextMonday), end: endOfDayMs(nextSunday) };
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start: start.getTime(), end: end.getTime() };
    }
    case 'custom':
      if (!customStart || !customEnd) return null;
      return { start: startOfDayMs(customStart), end: endOfDayMs(customEnd) };
    default:
      return null;
  }
};

export const isUnassignedAppPreOrder = (ride) =>
  ride.orderType === 1 &&
  !ride.isPhoneCustomer &&
  !ride.isRecurring &&
  !ride.driver &&
  !ride.driverId &&
  (ride.status === 'scheduled' || ride.status === 'open' || ride.status === 'dispatcher_pending');

export const isImminentPreAccepted = (ride, nowMs) => {
  if (ride.status !== 'pre_accepted') return false;
  const scheduledMs = toMs(ride.scheduledTime || ride.scheduled_time);
  if (scheduledMs === null) return true;
  return scheduledMs - nowMs <= 5 * 60 * 1000;
};

export const getRideReferenceMs = (ride) => {
  if (COMPLETED_RIDE_STATUSES.has(ride.status)) {
    return toMs(ride.completedTimestamp || ride.completionTimestamp)
      ?? toMs(ride.timestamp || ride.request_time);
  }
  return toMs(ride.scheduledTime || ride.scheduled_time)
    ?? toMs(ride.timestamp || ride.request_time);
};

export const getRideTier = (ride, nowMs) => {
  const { status } = ride;
  const scheduledMs = toMs(ride.scheduledTime || ride.scheduled_time);

  if (status === 'pre_accepted' && scheduledMs !== null && scheduledMs - nowMs <= 5 * 60 * 1000) return 1;
  if (status === 'open' || status === 'dispatcher_pending') return 2;
  if (status === 'accepted') return 3;
  if (status === 'in_progress' || status === 'arrived') return 4;
  if (status === 'pre_accepted') return 5;
  if (status === 'scheduled' && scheduledMs !== null && scheduledMs - nowMs <= 24 * 60 * 60 * 1000) return 6;
  if (status === 'scheduled' && isUnassignedAppPreOrder(ride)) return 8;
  if (status === 'scheduled') return 7;
  if (status === 'paused') return 10;
  return 9;
};

export const getRideSortKey = (ride, tier) => {
  const scheduled = toMs(ride.scheduledTime || ride.scheduled_time);
  const timestamp = toMs(ride.timestamp || ride.request_time) ?? 0;
  const completed = toMs(ride.completedTimestamp || ride.completionTimestamp);

  switch (tier) {
    case 1: return scheduled ?? Number.MAX_SAFE_INTEGER;
    case 2: return timestamp;
    case 3: return timestamp;
    case 4: return timestamp;
    case 5: return scheduled ?? Number.MAX_SAFE_INTEGER;
    case 6:
    case 7:
    case 8: return scheduled ?? Number.MAX_SAFE_INTEGER;
    case 9: return -(completed ?? timestamp);
    case 10:
    default: return scheduled ?? timestamp;
  }
};

export const shouldIncludeRideInTimeRange = (ride, bounds, nowMs) => {
  if (!bounds) return true;
  if (ACTIVE_RIDE_STATUSES.has(ride.status)) return true;
  if (isUnassignedAppPreOrder(ride)) return true;
  if (isImminentPreAccepted(ride, nowMs)) return true;

  const refMs = getRideReferenceMs(ride);
  return refMs !== null && refMs >= bounds.start && refMs <= bounds.end;
};

export const sortRidesForDispatch = (rides, nowMs) => [...rides].sort((a, b) => {
  const tierA = getRideTier(a, nowMs);
  const tierB = getRideTier(b, nowMs);
  if (tierA !== tierB) return tierA - tierB;
  return getRideSortKey(a, tierA) - getRideSortKey(b, tierB);
});

export const getLiveMapRelevantRides = (rides, nowMs = Date.now()) => {
  const bounds = getTodayPlus12hBounds(nowMs);
  const visibleRides = rides.filter((ride) => (
    !COMPLETED_RIDE_STATUSES.has(ride.status) &&
    shouldIncludeRideInTimeRange(ride, bounds, nowMs)
  ));
  return sortRidesForDispatch(visibleRides, nowMs);
};
